import { extractPhoneNumberDigits } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

import { type ApiResponse, post } from "../../api/api";
import { type AuthOtpErrorCode } from "../LoginSignUp/types";
import { IdentifyProviderEnum, identifyProviderSchema } from "./types";

const otpChannelSchema = z.enum(["sms", "call", "whatsapp"]);

export const otpRequestSchema = z.object({
  isSignup: z.boolean(),
  phone: z.string(),
  channel: otpChannelSchema.optional(),
});

export const backendMainotpSuccessResponseSchema = z.object({
  data: z.string().optional(),
  message: z.string(),
  /**
   * error is a string that is interspersed in the server code
   * See https://github.com/ClipboardHealth/clipboard-health/blob/development/projects/backend-main/src/routes/agentProfile/controller/requestOTP.ts#L42
   * TODO - should we use `z.enum` here? If so, we will need to increase
   * confidence about syncing all error codes with the API code.
   */
  error: z.string().optional(),
});

export const otpSuccessResponseSchema = backendMainotpSuccessResponseSchema.extend({
  identityProvider: identifyProviderSchema,
  isSignup: z.boolean().optional(),
});

export type OtpRequestChannel = z.infer<typeof otpChannelSchema>;
export type OtpRequest = z.infer<typeof otpRequestSchema>;
export type RequestOtpSuccessResponse = z.infer<typeof otpSuccessResponseSchema>;
export interface RequestOtpErrorResponse {
  message: string;
  error: AuthOtpErrorCode;
}

export const REQUEST_OTP_PATH = "/agentProfile/requestOTP";

export function useRequestOtp(
  options: UseMutationOptions<
    ApiResponse<RequestOtpSuccessResponse>,
    AxiosError<RequestOtpErrorResponse>,
    OtpRequest
  > = {}
): UseMutationResult<
  ApiResponse<RequestOtpSuccessResponse>,
  AxiosError<RequestOtpErrorResponse>,
  OtpRequest
> {
  return useMutation<
    ApiResponse<RequestOtpSuccessResponse>,
    AxiosError<RequestOtpErrorResponse>,
    OtpRequest
  >({
    mutationFn: async ({ isSignup, phone, channel }) => {
      const phoneNumber = extractPhoneNumberDigits(phone);

      const response = await post({
        url: REQUEST_OTP_PATH,
        data: { isSignup, phone: phoneNumber, channel },
        requestSchema: otpRequestSchema,
        responseSchema: backendMainotpSuccessResponseSchema,
      });

      return {
        ...response,
        data: otpSuccessResponseSchema.parse({
          ...response.data,
          identityProvider: IdentifyProviderEnum.firebase,
        }),
      };
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.REQUEST_OTP_FAILURE,
    },
    ...options,
  });
}
